import {
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { sanitizeText, schemas } from "@app/lib/ui-foundation";
import { z } from "zod";
import {
  type InviteStatus,
  createEventId,
  createSlug,
} from "./events-admin-utils";

const inviteStatusSchema = z.enum(["SENT", "OPENED", "ACCEPTED", "BOOKED"]);
const eventStatusSchema = z.enum(["Active", "Review", "Completed"]);
const groupTypeSchema = z.enum([
  "Corporate",
  "Wedding",
  "Sports",
  "Summit",
  "Conference",
]);
const paymentModelSchema = z.enum(["Organizer Pays", "Traveler Pays"]);
const engagementSchema = z.enum(["High", "Growing", "Needs Attention"]);
const featureCategorySchema = z.enum(["travel", "invitation", "event"]);

const inviteeSchema = z.object({
  id: schemas.sanitizedString,
  name: schemas.sanitizedString,
  email: schemas.email,
  status: inviteStatusSchema,
  recentActivity: schemas.sanitizedString,
});

const attendeeSchema = z.object({
  id: schemas.sanitizedString,
  name: schemas.sanitizedString,
  email: schemas.email,
});

const scheduleItemSchema = z.object({
  id: schemas.sanitizedString,
  title: schemas.sanitizedString,
  time: schemas.sanitizedString,
  description: schemas.sanitizedString.optional().default(""),
});

const scheduleDaySchema = z.object({
  id: schemas.sanitizedString,
  label: schemas.sanitizedString,
  isoDate: schemas.dateString,
  items: z.array(scheduleItemSchema),
});

const airlineAccessSchema = z.object({
  name: schemas.sanitizedString,
  enabled: z.boolean(),
  bookingsLabel: schemas.sanitizedString.optional(),
});

const passengerManifestEntrySchema = z.object({
  id: schemas.sanitizedString,
  name: schemas.sanitizedString,
  number: z.number().int().positive(),
  airline: schemas.sanitizedString,
  locator: schemas.sanitizedString,
  ticket: schemas.sanitizedString,
  amountPaid: schemas.sanitizedString,
});

const agreementSchema = z.object({
  fileName: schemas.sanitizedString,
  uploadedAt: schemas.sanitizedString,
  status: schemas.sanitizedString,
});

const eventRecordSchema = z.object({
  id: schemas.sanitizedString,
  slug: schemas.sanitizedString,
  groupName: schemas.sanitizedString,
  groupType: groupTypeSchema,
  destination: schemas.sanitizedString,
  origin: schemas.sanitizedString,
  venueName: schemas.sanitizedString,
  venueAddress: schemas.sanitizedString,
  organizerName: schemas.sanitizedString,
  organizerEmail: schemas.email,
  departDate: schemas.dateString,
  returnDate: schemas.dateString,
  passengers: z.number().int().positive(),
  bookedCount: z.number().int().nonnegative(),
  paymentModel: paymentModelSchema,
  status: eventStatusSchema,
  description: schemas.sanitizedString,
  inviteHeadline: schemas.sanitizedString,
  inviteLink: schemas.sanitizedString,
  showSchedule: z.boolean(),
  showRsvp: z.boolean(),
  backgroundStyle: z.enum(["lagoon", "harbor", "summit", "dawn"]),
  invitees: z.array(inviteeSchema),
  rsvpAttendees: z.array(attendeeSchema),
  schedule: z.array(scheduleDaySchema),
  airlineAccess: z.array(airlineAccessSchema),
  passengerManifest: z.array(passengerManifestEntrySchema),
  agreement: agreementSchema,
});

const organizerProgramSchema = z.object({
  id: schemas.sanitizedString,
  eventId: schemas.sanitizedString,
  name: schemas.sanitizedString,
  endsOn: schemas.sanitizedString,
});

const organizerSchema = z.object({
  id: schemas.sanitizedString,
  name: schemas.sanitizedString,
  email: schemas.email,
  events: z.number().int().nonnegative(),
  travelers: z.number().int().nonnegative(),
  bookingRate: z.number().int().nonnegative(),
  lifetimeBookings: z.number().int().nonnegative(),
  engagement: engagementSchema,
  programs: z.array(organizerProgramSchema),
});

const clientSchema = z.object({
  id: schemas.sanitizedString,
  name: schemas.sanitizedString,
  slug: schemas.sanitizedString,
  tier: z.enum(["Enterprise", "Professional", "Standard"]),
  status: z.enum(["Active", "Trial"]),
  events: z.number().int().nonnegative(),
  organizers: z.number().int().nonnegative(),
  travelers: z.number().int().nonnegative(),
  lastActivity: schemas.sanitizedString,
});

const healthMetricSchema = z.object({
  id: schemas.sanitizedString,
  label: schemas.sanitizedString,
  value: schemas.sanitizedString,
  target: schemas.sanitizedString,
  status: z.enum(["Healthy", "Warning"]),
  detailA: schemas.sanitizedString,
  detailAValue: schemas.sanitizedString,
  detailB: schemas.sanitizedString,
  detailBValue: schemas.sanitizedString,
  helper: schemas.sanitizedString,
});

const healthAlertSchema = z.object({
  id: schemas.sanitizedString,
  severity: z.enum(["Warning", "Info", "Critical"]),
  message: schemas.sanitizedString,
  service: schemas.sanitizedString,
  status: schemas.sanitizedString,
  occurredAt: schemas.sanitizedString,
});

const productFeatureSchema = z.object({
  id: schemas.sanitizedString,
  category: featureCategorySchema,
  label: schemas.sanitizedString,
  description: schemas.sanitizedString,
  enabled: z.boolean(),
  premium: z.boolean(),
});

const questionSummarySchema = z.object({
  label: schemas.sanitizedString,
  count: z.number().int().nonnegative(),
});

const productSetupSchema = z.object({
  leadProduct: schemas.sanitizedString,
  activeQuestions: z.number().int().nonnegative(),
  products: z.array(productFeatureSchema),
  questionSummary: z.array(questionSummarySchema),
});

const seedSchema = z.object({
  events: z.array(eventRecordSchema),
  organizers: z.array(organizerSchema),
  clients: z.array(clientSchema),
  systemHealth: z.object({
    metrics: z.array(healthMetricSchema),
    alerts: z.array(healthAlertSchema),
  }),
  productSetup: productSetupSchema,
});

const seedData = seedSchema.parse({
  events: [
    {
      id: "EVT-8582",
      slug: "apex-sales-summit-2026",
      groupName: "APEX Sales Summit 2026",
      groupType: "Summit",
      destination: "Geneva, CH",
      origin: "JFK",
      venueName: "Hotel des Bergues Geneva",
      venueAddress: "33 Quai des Bergues, Geneva 1201, Switzerland",
      organizerName: "Alex Thompson",
      organizerEmail: "a.thompson@apex.com",
      departDate: "2026-06-14",
      returnDate: "2026-06-17",
      passengers: 7,
      bookedCount: 0,
      paymentModel: "Organizer Pays",
      status: "Active",
      description:
        "Join us and save up to 5% on your oneworld member airline airfare!",
      inviteHeadline: "YOU'RE INVITED!",
      inviteLink: "https://group.oneworld.com/?=visit_atlanta_yourname/signup",
      showSchedule: false,
      showRsvp: true,
      backgroundStyle: "harbor",
      invitees: [
        {
          id: "invitee-bob",
          name: "Bob Jones",
          email: "bob.jones@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-jessica",
          name: "Jessica Johnson",
          email: "j.johnson@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-mark",
          name: "Mark Strous",
          email: "m.strous@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-alice",
          name: "Alice Smith",
          email: "alice.smith@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-tom",
          name: "Tom Brown",
          email: "tom.brown@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-karen",
          name: "Karen White",
          email: "karen.white@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
        {
          id: "invitee-chris",
          name: "Chris Green",
          email: "chris.green@company.com",
          status: "SENT",
          recentActivity: "Just now",
        },
      ],
      rsvpAttendees: [
        { id: "rsvp-bob", name: "Bob Jones", email: "bob.jones@company.com" },
        {
          id: "rsvp-jessica",
          name: "Jessica Johnson",
          email: "j.johnson@company.com",
        },
        { id: "rsvp-mark", name: "Mark Strous", email: "m.strous@company.com" },
        {
          id: "rsvp-alice",
          name: "Alice Smith",
          email: "alice.smith@company.com",
        },
        { id: "rsvp-tom", name: "Tom Brown", email: "tom.brown@company.com" },
        {
          id: "rsvp-karen",
          name: "Karen White",
          email: "karen.white@company.com",
        },
        {
          id: "rsvp-chris",
          name: "Chris Green",
          email: "chris.green@company.com",
        },
      ],
      schedule: [
        {
          id: "evt-8582-day-1",
          label: "Sun, Jun 14",
          isoDate: "2026-06-14",
          items: [],
        },
        {
          id: "evt-8582-day-2",
          label: "Mon, Jun 15",
          isoDate: "2026-06-15",
          items: [],
        },
        {
          id: "evt-8582-day-3",
          label: "Tue, Jun 16",
          isoDate: "2026-06-16",
          items: [],
        },
        {
          id: "evt-8582-day-4",
          label: "Wed, Jun 17",
          isoDate: "2026-06-17",
          items: [],
        },
      ],
      airlineAccess: [
        { name: "Alaska Airlines", enabled: true },
        {
          name: "American Airlines",
          enabled: true,
          bookingsLabel: "8 bookings for $5,879 USD",
        },
        {
          name: "British Airways",
          enabled: true,
          bookingsLabel: "5 bookings for 1,897 GBP",
        },
        {
          name: "Cathay Pacific",
          enabled: true,
          bookingsLabel: "4 bookings for HK$15,200",
        },
        { name: "Fiji Airways", enabled: true },
        { name: "Finnair", enabled: true },
        { name: "Iberia", enabled: true },
        { name: "Japan Airlines", enabled: true },
        { name: "Malaysia Airlines", enabled: true },
        { name: "Qantas", enabled: true },
        {
          name: "Qatar Airways",
          enabled: true,
          bookingsLabel: "5 bookings for QR 12,450",
        },
        { name: "Royal Jordanian", enabled: true },
        { name: "SriLankan Airlines", enabled: true },
      ],
      passengerManifest: [
        {
          id: "evt-8582-manifest-1",
          name: "Jones, Bob",
          number: 1,
          airline: "AA",
          locator: "AA8582A",
          ticket: "00112345601",
          amountPaid: "$1,245.00 USD",
        },
        {
          id: "evt-8582-manifest-2",
          name: "Johnson, Jessica",
          number: 1,
          airline: "BA",
          locator: "BA8582B",
          ticket: "00198765602",
          amountPaid: "£980.00 GBP",
        },
        {
          id: "evt-8582-manifest-3",
          name: "Strous, Mark",
          number: 1,
          airline: "AA",
          locator: "AA8582C",
          ticket: "00176543603",
          amountPaid: "$1,245.00 USD",
        },
        {
          id: "evt-8582-manifest-4",
          name: "Green, Chris",
          number: 1,
          airline: "QR",
          locator: "QR8582D",
          ticket: "00118899604",
          amountPaid: "QR 4,500",
        },
      ],
      agreement: {
        fileName: "Group_Travel_Agreement_AA1240.pdf",
        uploadedAt: "Uploaded on Feb 10, 2025",
        status: "Signed & verified",
      },
    },
    {
      id: "EVT-8291",
      slug: "apex-sales-summit-2026-live",
      groupName: "APEX Sales Summit 2026",
      groupType: "Summit",
      destination: "Geneva",
      origin: "JFK",
      venueName: "Hotel des Bergues Geneva",
      venueAddress: "33 Quai des Bergues, Geneva 1201, Switzerland",
      organizerName: "Alex Thompson",
      organizerEmail: "a.thompson@apex.com",
      departDate: "2026-06-14",
      returnDate: "2026-06-17",
      passengers: 35,
      bookedCount: 20,
      paymentModel: "Organizer Pays",
      status: "Active",
      description:
        "Join us and save up to 5% on your oneworld member airline airfare!",
      inviteHeadline: "YOU'RE INVITED!",
      inviteLink: "https://group.oneworld.com/?=visit_atlanta_yourname/signup",
      showSchedule: true,
      showRsvp: true,
      backgroundStyle: "lagoon",
      invitees: [
        {
          id: "invitee-1",
          name: "Bob Jones",
          email: "bob.jones@company.com",
          status: "BOOKED",
          recentActivity: "2h ago",
        },
        {
          id: "invitee-2",
          name: "Jessica Johnson",
          email: "j.johnson@company.com",
          status: "ACCEPTED",
          recentActivity: "2h ago",
        },
        {
          id: "invitee-3",
          name: "Mark Strous",
          email: "m.strous@company.com",
          status: "OPENED",
          recentActivity: "12h ago",
        },
        {
          id: "invitee-4",
          name: "Alice Smith",
          email: "alice.smith@company.com",
          status: "SENT",
          recentActivity: "3d ago",
        },
        {
          id: "invitee-5",
          name: "Tom Brown",
          email: "tom.brown@company.com",
          status: "SENT",
          recentActivity: "3d ago",
        },
        {
          id: "invitee-6",
          name: "Karen White",
          email: "karen.white@company.com",
          status: "OPENED",
          recentActivity: "12h ago",
        },
        {
          id: "invitee-7",
          name: "Chris Green",
          email: "chris.green@company.com",
          status: "ACCEPTED",
          recentActivity: "2h ago",
        },
      ],
      rsvpAttendees: [
        { id: "rsvp-1", name: "Bob Jones", email: "bob.jones@company.com" },
        {
          id: "rsvp-2",
          name: "Jessica Johnson",
          email: "j.johnson@company.com",
        },
        { id: "rsvp-3", name: "Mark Strous", email: "m.strous@company.com" },
        { id: "rsvp-4", name: "Alice Smith", email: "alice.smith@company.com" },
        { id: "rsvp-5", name: "Tom Brown", email: "tom.brown@company.com" },
        { id: "rsvp-6", name: "Karen White", email: "karen.white@company.com" },
        { id: "rsvp-7", name: "Chris Green", email: "chris.green@company.com" },
      ],
      schedule: [
        {
          id: "evt-8291-day-1",
          label: "Sun, Jun 14",
          isoDate: "2026-06-14",
          items: [
            {
              id: "evt-8291-item-1",
              title: "Event Start",
              time: "9:00am",
              description: "",
            },
            {
              id: "evt-8291-item-2",
              title: "Group Arrival/Hotel Check-in",
              time: "12:00pm",
              description: "",
            },
            {
              id: "evt-8291-item-3",
              title: "Welcome Reception & Cocktails",
              time: "6:00pm",
              description: "",
            },
            {
              id: "evt-8291-item-4",
              title: "Evening Cruise on Lake Geneva",
              time: "8:00pm",
              description: "",
            },
          ],
        },
        {
          id: "evt-8291-day-2",
          label: "Mon, Jun 15",
          isoDate: "2026-06-15",
          items: [
            {
              id: "evt-8291-item-5",
              title: "Guided Tour Old Town (Vieille Ville)",
              time: "1:00pm",
              description: "",
            },
            {
              id: "evt-8291-item-6",
              title: "Group Brunch",
              time: "10:30am",
              description: "",
            },
            {
              id: "evt-8291-item-7",
              title: "Visit Jet d'Eau & Waterfront",
              time: "2:30pm",
              description: "",
            },
            {
              id: "evt-8291-item-8",
              title: "Swiss Fondue Dinner",
              time: "6:30pm",
              description: "",
            },
          ],
        },
        {
          id: "evt-8291-day-3",
          label: "Tue, Jun 16",
          isoDate: "2026-06-16",
          items: [
            {
              id: "evt-8291-item-9",
              title: "Visit UN Palace & International Museum",
              time: "10:30am",
              description: "",
            },
            {
              id: "evt-8291-item-10",
              title: "Free Time + Lunch in Carouge District",
              time: "12:30pm",
              description: "",
            },
            {
              id: "evt-8291-item-11",
              title: "Farewell Dinner at Bayview Restaurant",
              time: "5:30pm",
              description: "",
            },
            {
              id: "evt-8291-item-12",
              title: "Morning Walk along Lake Geneva",
              time: "7:30am",
              description: "",
            },
            {
              id: "evt-8291-item-13",
              title: "Breakfast Buffet at hotel",
              time: "8:30am",
              description: "",
            },
          ],
        },
        {
          id: "evt-8291-day-4",
          label: "Wed, Jun 17",
          isoDate: "2026-06-17",
          items: [
            {
              id: "evt-8291-item-14",
              title: "Early Breakfast",
              time: "6:00am",
              description: "",
            },
            {
              id: "evt-8291-item-15",
              title: "Head out",
              time: "7:00am",
              description: "",
            },
            {
              id: "evt-8291-item-16",
              title: "Event End",
              time: "",
              description: "",
            },
          ],
        },
      ],
      airlineAccess: [
        { name: "Alaska Airlines", enabled: true },
        {
          name: "American Airlines",
          enabled: true,
          bookingsLabel: "8 bookings for $5,879 USD",
        },
        {
          name: "British Airways",
          enabled: true,
          bookingsLabel: "5 bookings for 1,897 GBP",
        },
        {
          name: "Cathay Pacific",
          enabled: true,
          bookingsLabel: "4 bookings for HK$15,200",
        },
        { name: "Fiji Airways", enabled: true },
        { name: "Finnair", enabled: true },
        { name: "Iberia", enabled: true },
        { name: "Japan Airlines", enabled: true },
        { name: "Malaysia Airlines", enabled: true },
        { name: "Oman Air", enabled: true },
        { name: "Qantas", enabled: true },
        {
          name: "Qatar Airways",
          enabled: true,
          bookingsLabel: "5 bookings for QR 12,450",
        },
        { name: "Royal Air Maroc", enabled: true },
        { name: "Royal Jordanian", enabled: true },
        { name: "SriLankan Airlines", enabled: true },
      ],
      passengerManifest: [
        {
          id: "manifest-1",
          name: "Thompson, Alex",
          number: 1,
          airline: "AA",
          locator: "ABC123",
          ticket: "00123984210",
          amountPaid: "$734.87 USD",
        },
        {
          id: "manifest-2",
          name: "Chen, Jessica",
          number: 1,
          airline: "AA",
          locator: "ABC124",
          ticket: "00123984211",
          amountPaid: "$734.87 USD",
        },
        {
          id: "manifest-3",
          name: "Miller, David",
          number: 1,
          airline: "AA",
          locator: "ABC125",
          ticket: "00123984212",
          amountPaid: "$734.87 USD",
        },
        {
          id: "manifest-4",
          name: "Garcia, Maria",
          number: 1,
          airline: "AA",
          locator: "ABC126",
          ticket: "00123984213",
          amountPaid: "$734.87 USD",
        },
        {
          id: "manifest-5",
          name: "Taylor, Robert",
          number: 1,
          airline: "AA",
          locator: "ABC127",
          ticket: "00123984214",
          amountPaid: "$734.87 USD",
        },
        {
          id: "manifest-6",
          name: "White, Lisa",
          number: 1,
          airline: "BA",
          locator: "Z9X7R3",
          ticket: "00123984219",
          amountPaid: "£759.66 GBP",
        },
        {
          id: "manifest-7",
          name: "Martin, Jennifer",
          number: 1,
          airline: "BA",
          locator: "Z9X7R5",
          ticket: "00123984221",
          amountPaid: "£379.40 GBP",
        },
        {
          id: "manifest-8",
          name: "Young, Jason",
          number: 1,
          airline: "CX",
          locator: "KLM889",
          ticket: "00123984226",
          amountPaid: "HK$3,800 HKD",
        },
      ],
      agreement: {
        fileName: "Group_Travel_Agreement_AA1240.pdf",
        uploadedAt: "Uploaded on Feb 10, 2025",
        status: "Signed & verified",
      },
    },
    {
      id: "EVT-7542",
      slug: "global-biotech-forum",
      groupName: "Global Biotech Forum",
      groupType: "Conference",
      destination: "London, UK",
      origin: "ORD",
      venueName: "The Savoy London",
      venueAddress: "Strand, London WC2R 0EZ, United Kingdom",
      organizerName: "Sarah Jenkins",
      organizerEmail: "s.jenkins@globex.com",
      departDate: "2026-08-22",
      returnDate: "2026-08-28",
      passengers: 120,
      bookedCount: 0,
      paymentModel: "Traveler Pays",
      status: "Review",
      description:
        "A global forum connecting biotech leaders across Europe and North America.",
      inviteHeadline: "SAVE THE DATE",
      inviteLink: "https://group.oneworld.com/global-biotech-forum",
      showSchedule: false,
      showRsvp: false,
      backgroundStyle: "dawn",
      invitees: [
        {
          id: "invitee-7542-hiro",
          name: "Hiro Nakamura",
          email: "h.nakamura@gbf.org",
          status: "ACCEPTED",
          recentActivity: "4h ago",
        },
        {
          id: "invitee-7542-ava",
          name: "Ava Martin",
          email: "ava.martin@gbf.org",
          status: "OPENED",
          recentActivity: "1d ago",
        },
        {
          id: "invitee-7542-luca",
          name: "Luca Bianchi",
          email: "l.bianchi@gbf.org",
          status: "SENT",
          recentActivity: "3d ago",
        },
        {
          id: "invitee-7542-mei",
          name: "Mei Chen",
          email: "m.chen@gbf.org",
          status: "BOOKED",
          recentActivity: "6h ago",
        },
      ],
      rsvpAttendees: [],
      schedule: [],
      airlineAccess: [
        {
          name: "British Airways",
          enabled: true,
          bookingsLabel: "12 bookings for £9,420 GBP",
        },
        {
          name: "American Airlines",
          enabled: true,
          bookingsLabel: "6 bookings for $7,210 USD",
        },
        { name: "Iberia", enabled: true },
        { name: "Finnair", enabled: true },
        {
          name: "Qatar Airways",
          enabled: true,
          bookingsLabel: "3 bookings for QR 8,100",
        },
        { name: "Royal Jordanian", enabled: true },
        { name: "SriLankan Airlines", enabled: true },
      ],
      passengerManifest: [
        {
          id: "evt-7542-manifest-1",
          name: "Nakamura, Hiro",
          number: 1,
          airline: "BA",
          locator: "BA7542A",
          ticket: "00125001601",
          amountPaid: "£785.00 GBP",
        },
        {
          id: "evt-7542-manifest-2",
          name: "Martin, Ava",
          number: 1,
          airline: "BA",
          locator: "BA7542B",
          ticket: "00125001602",
          amountPaid: "£785.00 GBP",
        },
        {
          id: "evt-7542-manifest-3",
          name: "Chen, Mei",
          number: 1,
          airline: "AA",
          locator: "AA7542C",
          ticket: "00125001603",
          amountPaid: "$1,150.00 USD",
        },
      ],
      agreement: {
        fileName: "Global_Biotech_Forum_Agreement.pdf",
        uploadedAt: "Uploaded on Feb 28, 2025",
        status: "Pending signature",
      },
    },
    {
      id: "EVT-7010",
      slug: "leadership-offsite-2025",
      groupName: "Leadership Offsite 2025",
      groupType: "Corporate",
      destination: "Tokyo, JP",
      origin: "LAX",
      venueName: "Aman Tokyo",
      venueAddress: "The Otemachi Tower, 1-5-6 Otemachi, Tokyo",
      organizerName: "Michael Chen",
      organizerEmail: "m.chen@northstar.com",
      departDate: "2025-09-10",
      returnDate: "2025-09-14",
      passengers: 24,
      bookedCount: 24,
      paymentModel: "Organizer Pays",
      status: "Completed",
      description: "Thank you for joining our leadership summit in Tokyo.",
      inviteHeadline: "THANK YOU FOR ATTENDING",
      inviteLink: "https://group.oneworld.com/leadership-offsite-2025",
      showSchedule: true,
      showRsvp: false,
      backgroundStyle: "summit",
      invitees: [],
      rsvpAttendees: [],
      schedule: [],
      airlineAccess: [],
      passengerManifest: [],
      agreement: {
        fileName: "Leadership_Offsite_Agreement.pdf",
        uploadedAt: "Uploaded on Jul 12, 2025",
        status: "Archived",
      },
    },
  ],
  organizers: [
    {
      id: "ORG-001",
      name: "Alex Thompson",
      email: "alex.t@corporate.com",
      events: 12,
      travelers: 450,
      bookingRate: 88,
      lifetimeBookings: 1248,
      engagement: "High",
      programs: [
        {
          id: "program-1",
          eventId: "EVT-8291",
          name: "Summit Program 1",
          endsOn: "Ends Oct 2025",
        },
        {
          id: "program-2",
          eventId: "EVT-8582",
          name: "Summit Program 2",
          endsOn: "Ends Oct 2026",
        },
        {
          id: "program-3",
          eventId: "EVT-7542",
          name: "Summit Program 3",
          endsOn: "Ends Oct 2027",
        },
      ],
    },
    {
      id: "ORG-002",
      name: "Sarah Jenkins",
      email: "s.jenkins@globex.com",
      events: 8,
      travelers: 210,
      bookingRate: 72,
      lifetimeBookings: 1248,
      engagement: "Growing",
      programs: [
        {
          id: "program-4",
          eventId: "EVT-7542",
          name: "Summit Program 1",
          endsOn: "Ends Oct 2025",
        },
        {
          id: "program-5",
          eventId: "EVT-8582",
          name: "Summit Program 2",
          endsOn: "Ends Oct 2026",
        },
        {
          id: "program-6",
          eventId: "EVT-8291",
          name: "Summit Program 3",
          endsOn: "Ends Oct 2027",
        },
      ],
    },
    {
      id: "ORG-003",
      name: "Michael Chen",
      email: "m.chen@northstar.com",
      events: 15,
      travelers: 680,
      bookingRate: 94,
      lifetimeBookings: 1895,
      engagement: "High",
      programs: [
        {
          id: "program-7",
          eventId: "EVT-7010",
          name: "Leadership Offsite",
          endsOn: "Ends Sep 2025",
        },
      ],
    },
    {
      id: "ORG-004",
      name: "David Miller",
      email: "d.miller@vertex.com",
      events: 5,
      travelers: 95,
      bookingRate: 45,
      lifetimeBookings: 302,
      engagement: "Needs Attention",
      programs: [
        {
          id: "program-8",
          eventId: "EVT-7542",
          name: "Biotech Forum",
          endsOn: "Ends Aug 2026",
        },
      ],
    },
    {
      id: "ORG-005",
      name: "Emma Wilson",
      email: "e.wilson@atlas.com",
      events: 9,
      travelers: 310,
      bookingRate: 81,
      lifetimeBookings: 964,
      engagement: "High",
      programs: [
        {
          id: "program-9",
          eventId: "EVT-8291",
          name: "APEX Summit Europe",
          endsOn: "Ends Jun 2026",
        },
      ],
    },
  ],
  clients: [
    {
      id: "client-1",
      name: "oneworld Alliance",
      slug: "oneworld",
      tier: "Enterprise",
      status: "Active",
      events: 24,
      organizers: 156,
      travelers: 3420,
      lastActivity: "2 hours ago",
    },
    {
      id: "client-2",
      name: "Star Alliance",
      slug: "staralliance",
      tier: "Enterprise",
      status: "Active",
      events: 18,
      organizers: 98,
      travelers: 2100,
      lastActivity: "5 hours ago",
    },
    {
      id: "client-3",
      name: "SkyTeam",
      slug: "skyteam",
      tier: "Professional",
      status: "Active",
      events: 12,
      organizers: 64,
      travelers: 1580,
      lastActivity: "1 day ago",
    },
    {
      id: "client-4",
      name: "Demo Airline Co.",
      slug: "demo-airline",
      tier: "Standard",
      status: "Trial",
      events: 3,
      organizers: 12,
      travelers: 150,
      lastActivity: "3 days ago",
    },
  ],
  systemHealth: {
    metrics: [
      {
        id: "metric-api",
        label: "API Response Time",
        value: "142ms",
        target: "< 200ms",
        status: "Healthy",
        detailA: "Last hour average",
        detailAValue: "142ms",
        detailB: "24h peak",
        detailBValue: "198ms",
        helper: "Average response time across all endpoints",
      },
      {
        id: "metric-db",
        label: "Database Performance",
        value: "98.7%",
        target: "> 95%",
        status: "Healthy",
        detailA: "Last hour average",
        detailAValue: "98.7%",
        detailB: "24h peak",
        detailBValue: "98.7%",
        helper: "Query execution success rate",
      },
      {
        id: "metric-uptime",
        label: "System Uptime",
        value: "99.98%",
        target: "> 99.9%",
        status: "Healthy",
        detailA: "Last hour average",
        detailAValue: "99.98%",
        detailB: "24h peak",
        detailBValue: "99.98%",
        helper: "Last 30 days availability",
      },
      {
        id: "metric-memory",
        label: "Memory Usage",
        value: "78%",
        target: "< 80%",
        status: "Warning",
        detailA: "Last hour average",
        detailAValue: "78%",
        detailB: "24h peak",
        detailBValue: "82%",
        helper: "Current server memory utilization",
      },
      {
        id: "metric-errors",
        label: "Error Rate",
        value: "0.03%",
        target: "< 0.1%",
        status: "Healthy",
        detailA: "Last hour average",
        detailAValue: "0.03%",
        detailB: "24h peak",
        detailBValue: "0.03%",
        helper: "Application error frequency",
      },
      {
        id: "metric-users",
        label: "Active Users",
        value: "2,847",
        target: "< 5,000",
        status: "Healthy",
        detailA: "Last hour average",
        detailAValue: "2,847",
        detailB: "24h peak",
        detailBValue: "2,847",
        helper: "Currently connected users",
      },
    ],
    alerts: [
      {
        id: "alert-1",
        severity: "Warning",
        message: "Memory usage exceeded 75% threshold",
        service: "App Server 3",
        status: "monitoring",
        occurredAt: "2026-02-23 14:32",
      },
      {
        id: "alert-2",
        severity: "Info",
        message: "Scheduled database maintenance completed",
        service: "Primary DB",
        status: "resolved",
        occurredAt: "2026-02-23 13:15",
      },
      {
        id: "alert-3",
        severity: "Critical",
        message: "API rate limit reached for external service",
        service: "Payment Gateway",
        status: "resolved",
        occurredAt: "2026-02-23 11:48",
      },
    ],
  },
  productSetup: {
    leadProduct: "flights",
    activeQuestions: 7,
    questionSummary: [
      { label: "Event Setup", count: 3 },
      { label: "Traveler Registration", count: 4 },
    ],
    products: [
      {
        id: "flights",
        category: "travel",
        label: "Flights",
        description: "Air travel booking and management",
        enabled: true,
        premium: false,
      },
      {
        id: "hotel",
        category: "travel",
        label: "Hotel",
        description: "Accommodation booking and management",
        enabled: true,
        premium: false,
      },
      {
        id: "transportation",
        category: "travel",
        label: "Transportation",
        description: "Transfers, shuttles, and rental cars",
        enabled: true,
        premium: false,
      },
      {
        id: "travel-insurance",
        category: "travel",
        label: "Travel Insurance",
        description: "Trip protection and coverage",
        enabled: true,
        premium: false,
      },
      {
        id: "qr-code",
        category: "invitation",
        label: "QR Code",
        description: "Generate QR codes for event invitations",
        enabled: true,
        premium: false,
      },
      {
        id: "email-invitations",
        category: "invitation",
        label: "Email Invitations",
        description: "Send email invitations to attendees",
        enabled: true,
        premium: false,
      },
      {
        id: "sms-invitations",
        category: "invitation",
        label: "SMS Invitations",
        description: "Send SMS invitations to attendees",
        enabled: true,
        premium: true,
      },
      {
        id: "custom-branding",
        category: "invitation",
        label: "Custom Branding",
        description: "Add custom branding to invitations",
        enabled: true,
        premium: true,
      },
      {
        id: "rsvp",
        category: "event",
        label: "RSVP",
        description: "Allow guests to respond to event invitations",
        enabled: true,
        premium: false,
      },
      {
        id: "schedule",
        category: "event",
        label: "Schedule",
        description: "Display event schedule and timeline",
        enabled: true,
        premium: false,
      },
      {
        id: "speakers",
        category: "event",
        label: "Speakers",
        description: "Showcase event speakers and presenters",
        enabled: false,
        premium: false,
      },
      {
        id: "attendee-chat",
        category: "event",
        label: "Attendee Chat",
        description: "Enable chat between attendees",
        enabled: false,
        premium: false,
      },
    ],
  },
});

type SeedData = z.infer<typeof seedSchema>;

export type EventRecord = SeedData["events"][number];
export type OrganizerRecord = SeedData["organizers"][number];
export type ClientRecord = SeedData["clients"][number];
export type HealthMetricRecord = SeedData["systemHealth"]["metrics"][number];
export type HealthAlertRecord = SeedData["systemHealth"]["alerts"][number];
export type ProductFeatureRecord = SeedData["productSetup"]["products"][number];
export type ProductSetupRecord = SeedData["productSetup"];
export type ScheduleDayRecord = EventRecord["schedule"][number];
export type ScheduleItemRecord = ScheduleDayRecord["items"][number];
export type InviteeRecord = EventRecord["invitees"][number];

export type CreateEventInput = {
  groupName: string;
  groupType: string;
  organizerName: string;
  organizerEmail: string;
  venueName: string;
  venueAddress: string;
  origin: string;
  destination: string;
  departDate: string;
  returnDate: string;
  passengers: number;
};

type ScheduleItemInput = {
  dayId: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
};

type InviteCrewInput = {
  name: string;
  email: string;
};

type EventUpdatePatch = Partial<
  Pick<
    EventRecord,
    | "groupName"
    | "description"
    | "destination"
    | "venueName"
    | "venueAddress"
    | "showSchedule"
  >
>;

export type AddClientInput = {
  name: string;
  tier: "Enterprise" | "Professional" | "Standard";
  status: "Active" | "Trial";
};

export type AddOrganizerInput = {
  name: string;
  email: string;
};

export type MessageUserInput = {
  subject: string;
  message: string;
};

export type AddPassengerInput = {
  name: string;
  airline: string;
  locator: string;
  ticket: string;
  amountPaid: string;
};

export type EditGroupInput = {
  groupName: string;
  groupType: string;
  destination: string;
  organizerName: string;
  organizerEmail: string;
  venueName: string;
  venueAddress: string;
  departDate: string;
  returnDate: string;
  passengers: number;
  paymentModel: string;
};

type EventsAdminDemoContextValue = SeedData & {
  createEvent: (input: CreateEventInput) => EventRecord;
  updateEvent: (eventId: string, patch: EventUpdatePatch) => void;
  addScheduleItem: (eventId: string, input: ScheduleItemInput) => void;
  addInvitees: (eventId: string, invitees: InviteCrewInput[]) => void;
  remindInvitee: (eventId: string, inviteeId: string) => void;
  toggleFeature: (featureId: string) => void;
  setLeadProduct: (featureId: string) => void;
  getEventById: (eventId: string) => EventRecord | undefined;
  addClient: (input: AddClientInput) => void;
  addOrganizer: (input: AddOrganizerInput) => void;
  addPassenger: (eventId: string, input: AddPassengerInput) => void;
  editGroup: (eventId: string, input: EditGroupInput) => void;
  bulkUploadPassengers: (
    eventId: string,
    passengers: AddPassengerInput[],
  ) => void;
};

const EventsAdminDemoContext =
  createContext<EventsAdminDemoContextValue | null>(null);

const sharedAirlineAccess: EventRecord["airlineAccess"] =
  seedData.events.find((eventItem) => eventItem.id === "EVT-8291")
    ?.airlineAccess ?? [];
const sharedPassengerManifest: EventRecord["passengerManifest"] =
  seedData.events.find((eventItem) => eventItem.id === "EVT-8291")
    ?.passengerManifest ?? [];
const sharedAgreement: EventRecord["agreement"] =
  seedData.events.find((eventItem) => eventItem.id === "EVT-8291")?.agreement ??
  seedData.events[0].agreement;

function sanitizePatchValue<T>(value: T) {
  return typeof value === "string" ? sanitizeText(value) : value;
}

export function EventsAdminDemoProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<EventRecord[]>(seedData.events);
  const [productSetup, setProductSetup] = useState<ProductSetupRecord>(
    seedData.productSetup,
  );
  const [clients, setClients] = useState<ClientRecord[]>(seedData.clients);
  const [organizers, setOrganizers] = useState<OrganizerRecord[]>(
    seedData.organizers,
  );

  const getEventById = (eventId: string) =>
    events.find((eventItem) => eventItem.id === eventId);

  const createEvent = (input: CreateEventInput) => {
    const eventId = createEventId(events.map((eventItem) => eventItem.id));
    const sanitizedName = sanitizeText(input.groupName);
    const createdEvent = eventRecordSchema.parse({
      id: eventId,
      slug: createSlug(sanitizedName),
      groupName: sanitizedName,
      groupType: input.groupType,
      destination: sanitizeText(input.destination),
      origin: sanitizeText(input.origin),
      venueName: sanitizeText(input.venueName),
      venueAddress: sanitizeText(input.venueAddress),
      organizerName: sanitizeText(input.organizerName),
      organizerEmail: input.organizerEmail.trim(),
      departDate: input.departDate,
      returnDate: input.returnDate,
      passengers: input.passengers,
      bookedCount: 0,
      paymentModel: "Organizer Pays",
      status: "Active",
      description:
        "Join us and save up to 5% on your oneworld member airline airfare!",
      inviteHeadline: "YOU'RE INVITED!",
      inviteLink: `https://group.oneworld.com/${createSlug(sanitizedName)}`,
      showSchedule: false,
      showRsvp: true,
      backgroundStyle: "harbor",
      invitees: [],
      rsvpAttendees: [],
      schedule: [
        {
          id: `${eventId}-day-1`,
          label: "Day 1",
          isoDate: input.departDate,
          items: [],
        },
        {
          id: `${eventId}-day-2`,
          label: "Day 2",
          isoDate: input.returnDate,
          items: [],
        },
      ],
      airlineAccess: sharedAirlineAccess,
      passengerManifest: sharedPassengerManifest,
      agreement: sharedAgreement,
    });

    setEvents((currentEvents) => [createdEvent, ...currentEvents]);
    return createdEvent;
  };

  const updateEvent = (eventId: string, patch: EventUpdatePatch) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) {
          return eventItem;
        }

        return eventRecordSchema.parse({
          ...eventItem,
          ...Object.fromEntries(
            Object.entries(patch).map(([key, value]) => [
              key,
              sanitizePatchValue(value),
            ]),
          ),
        });
      }),
    );
  };

  const addScheduleItem = (eventId: string, input: ScheduleItemInput) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) {
          return eventItem;
        }

        return eventRecordSchema.parse({
          ...eventItem,
          showSchedule: true,
          schedule: eventItem.schedule.map((day) => {
            if (day.id !== input.dayId) {
              return day;
            }

            return scheduleDaySchema.parse({
              ...day,
              items: [
                ...day.items,
                scheduleItemSchema.parse({
                  id: `${day.id}-${day.items.length + 1}`,
                  title: sanitizeText(input.title),
                  time: `${sanitizeText(input.startTime)} - ${sanitizeText(input.endTime)}`,
                  description: sanitizeText(input.description),
                }),
              ],
            });
          }),
        });
      }),
    );
  };

  const addInvitees = (eventId: string, invitees: InviteCrewInput[]) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) {
          return eventItem;
        }

        const createdInvitees = invitees.map((invitee, index) =>
          inviteeSchema.parse({
            id: `${eventId}-invitee-${eventItem.invitees.length + index + 1}`,
            name: sanitizeText(invitee.name),
            email: invitee.email.trim(),
            status: "SENT" satisfies InviteStatus,
            recentActivity: "Just now",
          }),
        );

        return eventRecordSchema.parse({
          ...eventItem,
          invitees: [...eventItem.invitees, ...createdInvitees],
        });
      }),
    );
  };

  const remindInvitee = (eventId: string, inviteeId: string) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) {
          return eventItem;
        }

        return eventRecordSchema.parse({
          ...eventItem,
          invitees: eventItem.invitees.map((invitee) =>
            invitee.id === inviteeId
              ? inviteeSchema.parse({
                  ...invitee,
                  recentActivity: "Reminder sent just now",
                })
              : invitee,
          ),
        });
      }),
    );
  };

  const toggleFeature = (featureId: string) => {
    setProductSetup((currentSetup) =>
      productSetupSchema.parse({
        ...currentSetup,
        products: currentSetup.products.map((feature) =>
          feature.id === featureId
            ? { ...feature, enabled: !feature.enabled }
            : feature,
        ),
      }),
    );
  };

  const setLeadProduct = (featureId: string) => {
    setProductSetup((currentSetup) =>
      productSetupSchema.parse({
        ...currentSetup,
        leadProduct: featureId,
      }),
    );
  };

  const addClient = (input: AddClientInput) => {
    const newClient = clientSchema.parse({
      id: `client-${clients.length + 1}`,
      name: sanitizeText(input.name),
      slug: sanitizeText(input.name).toLowerCase().replace(/\s+/g, "-"),
      tier: input.tier,
      status: input.status,
      events: 0,
      organizers: 0,
      travelers: 0,
      lastActivity: "Just now",
    });
    setClients((current) => [newClient, ...current]);
  };

  const addOrganizer = (input: AddOrganizerInput) => {
    const newOrganizer = organizerSchema.parse({
      id: `ORG-${String(organizers.length + 1).padStart(3, "0")}`,
      name: sanitizeText(input.name),
      email: input.email.trim(),
      events: 0,
      travelers: 0,
      bookingRate: 0,
      lifetimeBookings: 0,
      engagement: "Growing",
      programs: [],
    });
    setOrganizers((current) => [newOrganizer, ...current]);
  };

  const addPassenger = (eventId: string, input: AddPassengerInput) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) return eventItem;
        const newPassenger = passengerManifestEntrySchema.parse({
          id: `manifest-${eventItem.passengerManifest.length + 1}`,
          name: sanitizeText(input.name),
          number: 1,
          airline: sanitizeText(input.airline),
          locator: sanitizeText(input.locator),
          ticket: sanitizeText(input.ticket),
          amountPaid: sanitizeText(input.amountPaid),
        });
        return eventRecordSchema.parse({
          ...eventItem,
          passengerManifest: [...eventItem.passengerManifest, newPassenger],
        });
      }),
    );
  };

  const editGroup = (eventId: string, input: EditGroupInput) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) return eventItem;
        return eventRecordSchema.parse({
          ...eventItem,
          groupName: sanitizeText(input.groupName),
          groupType: input.groupType,
          destination: sanitizeText(input.destination),
          organizerName: sanitizeText(input.organizerName),
          organizerEmail: input.organizerEmail.trim(),
          venueName: sanitizeText(input.venueName),
          venueAddress: sanitizeText(input.venueAddress),
          departDate: input.departDate,
          returnDate: input.returnDate,
          passengers: input.passengers,
          paymentModel: input.paymentModel,
        });
      }),
    );
  };

  const bulkUploadPassengers = (
    eventId: string,
    passengers: AddPassengerInput[],
  ) => {
    setEvents((currentEvents) =>
      currentEvents.map((eventItem) => {
        if (eventItem.id !== eventId) return eventItem;
        const newPassengers = passengers.map((p, index) =>
          passengerManifestEntrySchema.parse({
            id: `manifest-${eventItem.passengerManifest.length + index + 1}`,
            name: sanitizeText(p.name),
            number: 1,
            airline: sanitizeText(p.airline),
            locator: sanitizeText(p.locator),
            ticket: sanitizeText(p.ticket),
            amountPaid: sanitizeText(p.amountPaid),
          }),
        );
        return eventRecordSchema.parse({
          ...eventItem,
          passengerManifest: [...eventItem.passengerManifest, ...newPassengers],
        });
      }),
    );
  };

  const value = useMemo<EventsAdminDemoContextValue>(
    () => ({
      events,
      organizers,
      clients,
      systemHealth: seedData.systemHealth,
      productSetup,
      createEvent,
      updateEvent,
      addScheduleItem,
      addInvitees,
      remindInvitee,
      toggleFeature,
      setLeadProduct,
      getEventById,
      addClient,
      addOrganizer,
      addPassenger,
      editGroup,
      bulkUploadPassengers,
    }),
    [events, productSetup, clients, organizers],
  );

  return (
    <EventsAdminDemoContext.Provider value={value}>
      {children}
    </EventsAdminDemoContext.Provider>
  );
}

export function useEventsAdminDemo() {
  const context = useContext(EventsAdminDemoContext);

  if (!context) {
    throw new Error(
      "useEventsAdminDemo must be used within EventsAdminDemoProvider",
    );
  }

  return context;
}
